const FALLBACK_COMPANY_ID = 'default';
const companies = new Map();
const categories = new Map();
const systems = new Map();
const lazySystems = new Map();
const companyCategoryLinks = new Map();
const categorySystemLinks = new Map();
let systemRegistryRevision = 0;
let headerConfigLoaded = false;

const RESERVED_MULTI_SELECTION_FIELDS = new Set(['obstacleIds']);

function normalizeBoundaryFieldList(value) {
  if (value == null) return [];
  if (!Array.isArray(value)) return null;
  const fields = [];
  const seen = new Set();
  for (const candidate of value) {
    const field = String(candidate || '').trim();
    if (!field || !/^[A-Za-z_$][A-Za-z0-9_$]*$/.test(field) || seen.has(field)) return null;
    seen.add(field);
    fields.push(field);
  }
  return fields;
}

function validateSystemBoundaryClaims(system) {
  const systemId = String(system?.id || '').trim();
  const multiSelectionFields = normalizeBoundaryFieldList(system?.multiSelectionFields);
  const boxSelectionPassthroughFields = normalizeBoundaryFieldList(system?.boxSelectionPassthroughFields);
  if (!systemId || !multiSelectionFields || !boxSelectionPassthroughFields) return null;

  const ownClaims = new Set([...multiSelectionFields, ...boxSelectionPassthroughFields]);
  if (ownClaims.size !== multiSelectionFields.length + boxSelectionPassthroughFields.length) return null;
  if ([...ownClaims].some(field => RESERVED_MULTI_SELECTION_FIELDS.has(field))) return null;

  for (const registered of systems.values()) {
    if (registered.id === systemId) continue;
    const registeredClaims = [
      ...(registered.multiSelectionFields || []),
      ...(registered.boxSelectionPassthroughFields || [])
    ];
    if (registeredClaims.some(field => ownClaims.has(field))) return null;
  }

  const legacyClipboardKind = typeof system?.legacyClipboardKind === 'string'
    ? system.legacyClipboardKind.trim()
    : '';
  if (legacyClipboardKind) {
    const conflicts = [...systems.values()].some(registered => (
      registered.id !== systemId && registered.legacyClipboardKind === legacyClipboardKind
    ));
    if (conflicts) return null;
  }

  return Object.freeze({
    multiSelectionFields: Object.freeze(multiSelectionFields),
    boxSelectionPassthroughFields: Object.freeze(boxSelectionPassthroughFields),
    legacyClipboardKind: legacyClipboardKind || null
  });
}

function nextRevision(current) {
  return current >= Number.MAX_SAFE_INTEGER ? 1 : current + 1;
}

// Navigation-State entspricht der Header-/Einstellungsreihenfolge:
// Company -> Systemgruppe (Kategorie) -> System (Produkt). Die Zeichenansicht
// bleibt fest die Draufsicht; 3D wird als separates Visualisierungsmodal geoeffnet.
export const state = {
  activeCompanyId: null,
  activeCategoryId: null,
  activeSystemId: null
};

/**
 * Registriert eine Firma als oberste Header-Filterebene.
 *
 * Zweck:
 * - haelt Firmen als oberste Registry-Ebene getrennt
 * - bleibt generisch, damit Produktmodule nur ihre Firma referenzieren
 *
 * @param {Object} company - Firma mit `id` und sichtbarem `label`.
 * @sideEffects Erweitert die Registry um eine Firma fuer Header und Filterung.
 */
export function registerCompany(company) {
  if (!company?.id) return;
  companies.set(company.id, {
    id: company.id,
    label: company.label || company.name || company.id,
    logoSrc: company.logoSrc || null,
    logoAlt: company.logoAlt || company.label || company.name || company.id,
    active: company.active !== false
  });
  systemRegistryRevision = nextRevision(systemRegistryRevision);
}

/**
 * Beschreibt register Category im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} category - Eingabewert für register Category.
 * Seiteneffekte: Beachtet fachliche Hilfslogik im jeweiligen Modul; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
export function registerCategory(category) {
  if (!category?.id) return;
  const companyId = category.companyId || FALLBACK_COMPANY_ID;
  if (!companies.has(companyId)) registerCompany({ id: companyId, label: companyId });
  categories.set(category.id, {
    id: category.id,
    companyId,
    label: category.label || category.id,
    active: category.active !== false
  });
  systemRegistryRevision = nextRevision(systemRegistryRevision);
}

/**
 * Beschreibt register System im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} system - Eingabewert für register System.
 * Seiteneffekte: Beachtet fachliche Hilfslogik im jeweiligen Modul; Änderungen erfolgen je nach Aufrufer am übergebenen Objekt oder globalen State.
 */
export function registerSystem(system) {
  if (!system?.id) return false;
  const boundaryClaims = validateSystemBoundaryClaims(system);
  if (!boundaryClaims) {
    console.warn(`[system-registry] Systembeitrag mit kollidierenden oder ungueltigen Owner-Feldern verworfen: ${String(system?.id || '')}`);
    return false;
  }
  const label = system.name || system.label || system.id;
  const category = categories.get(system.categoryId);
  const companyId = system.companyId || category?.companyId || FALLBACK_COMPANY_ID;
  const hiddenFromNavigation = system.hiddenFromNavigation === true || system.meta?.hiddenFromNavigation === true;
  if (!hiddenFromNavigation && !companies.has(companyId)) registerCompany({ id: companyId, label: companyId });
  const render3D = typeof system.render3D === 'function'
    ? system.render3D
    : typeof system.build3D === 'function'
      ? system.build3D
      : null;
  const propertiesPanel = typeof system.getPropertiesPanel === 'function'
    ? system.getPropertiesPanel
    : typeof system.renderUI === 'function'
      ? system.renderUI
      : null;
  const toolActionHandler = typeof system.handleToolAction === 'function'
    ? system.handleToolAction
    : typeof system.handleToolbarAction === 'function'
      ? system.handleToolbarAction
      : null;
  const bomFactory = typeof system.getBomItems === 'function'
    ? system.getBomItems
    : typeof system.createBomRows === 'function'
      ? system.createBomRows
      : null;
  const bomScopeFactory = typeof system.getBomScopes === 'function'
    ? system.getBomScopes
    : null;
  systems.set(system.id, {
    id: system.id,
    categoryId: system.categoryId,
    companyId,
    name: label,
    label,
    icon: system.icon || null,
    active: system.active !== false,
    aliases: normalizeSystemAliases(system.aliases),
    meta: system.meta && typeof system.meta === 'object' ? { ...system.meta } : {},
    hiddenFromNavigation,
    toolbarButtons: Array.isArray(system.toolbarButtons) ? system.toolbarButtons : [],
    selectionPriority: Number.isFinite(Number(system.selectionPriority)) ? Number(system.selectionPriority) : 1000,
    createDefaultState: typeof system.createDefaultState === 'function'
      ? system.createDefaultState
      : typeof system.createState === 'function'
        ? system.createState
        : null,
    createState: typeof system.createState === 'function'
      ? system.createState
      : typeof system.createDefaultState === 'function'
        ? system.createDefaultState
        : null,
    normalizeState: typeof system.normalizeState === 'function' ? system.normalizeState : null,
    fitCamera: typeof system.fitCamera === 'function' ? system.fitCamera : null,
    getPropertiesPanel: propertiesPanel,
    renderUI: propertiesPanel,
    render2D: typeof system.render2D === 'function' ? system.render2D : null,
    render2DOverlay: typeof system.render2DOverlay === 'function' ? system.render2DOverlay : null,
    render3D,
    build3D: render3D,
    hitTest2D: typeof system.hitTest2D === 'function' ? system.hitTest2D : null,
    ownsDomTarget: typeof system.ownsDomTarget === 'function' ? system.ownsDomTarget : null,
    handleClick: typeof system.handleClick === 'function' ? system.handleClick : null,
    handleDoubleClick: typeof system.handleDoubleClick === 'function' ? system.handleDoubleClick : null,
    wantsClickBeforeHitTest: typeof system.wantsClickBeforeHitTest === 'function'
      ? system.wantsClickBeforeHitTest
      : null,
    shouldSuppressPointerDownTarget: typeof system.shouldSuppressPointerDownTarget === 'function'
      ? system.shouldSuppressPointerDownTarget
      : null,
    wantsPointerDownBeforeSelection: typeof system.wantsPointerDownBeforeSelection === 'function'
      ? system.wantsPointerDownBeforeSelection
      : null,
    wantsContextMenuCancel: typeof system.wantsContextMenuCancel === 'function'
      ? system.wantsContextMenuCancel
      : null,
    handlePointerDown: typeof system.handlePointerDown === 'function' ? system.handlePointerDown : null,
    handlePointerMove: typeof system.handlePointerMove === 'function' ? system.handlePointerMove : null,
    handlePointerUp: typeof system.handlePointerUp === 'function' ? system.handlePointerUp : null,
    wantsPointerMoveFrame: typeof system.wantsPointerMoveFrame === 'function'
      ? system.wantsPointerMoveFrame
      : null,
    dragTypeSurvivesPointerUp: typeof system.dragTypeSurvivesPointerUp === 'function'
      ? system.dragTypeSurvivesPointerUp
      : null,
    clearPointerPreview: typeof system.clearPointerPreview === 'function'
      ? system.clearPointerPreview
      : null,
    deleteSelection: typeof system.deleteSelection === 'function' ? system.deleteSelection : null,
    nudgeSelection: typeof system.nudgeSelection === 'function' ? system.nudgeSelection : null,
    deleteObjects: typeof system.deleteObjects === 'function' ? system.deleteObjects : null,
    hitBelongsToSelection: typeof system.hitBelongsToSelection === 'function' ? system.hitBelongsToSelection : null,
    createMoveSnapshot: typeof system.createMoveSnapshot === 'function' ? system.createMoveSnapshot : null,
    applyMoveSnapshot: typeof system.applyMoveSnapshot === 'function' ? system.applyMoveSnapshot : null,
    syncMoveSelection: typeof system.syncMoveSelection === 'function' ? system.syncMoveSelection : null,
    applyMovePreview: typeof system.applyMovePreview === 'function' ? system.applyMovePreview : null,
    clearMovePreview: typeof system.clearMovePreview === 'function' ? system.clearMovePreview : null,
    createClipboardPayload: typeof system.createClipboardPayload === 'function' ? system.createClipboardPayload : null,
    pasteClipboardPayload: typeof system.pasteClipboardPayload === 'function' ? system.pasteClipboardPayload : null,
    createClipboardPreview: typeof system.createClipboardPreview === 'function' ? system.createClipboardPreview : null,
    renderClipboardPreview: typeof system.renderClipboardPreview === 'function' ? system.renderClipboardPreview : null,
    selectClipboardObjects: typeof system.selectClipboardObjects === 'function' ? system.selectClipboardObjects : null,
    getClipboardEntry: typeof system.getClipboardEntry === 'function' ? system.getClipboardEntry : null,
    buildClipboardEntry: typeof system.buildClipboardEntry === 'function' ? system.buildClipboardEntry : null,
    cancelInteraction: typeof system.cancelInteraction === 'function' ? system.cancelInteraction : null,
    clearTransientState: typeof system.clearTransientState === 'function' ? system.clearTransientState : null,
    clearSelection: typeof system.clearSelection === 'function' ? system.clearSelection : null,
    drawInteractionOverlay: typeof system.drawInteractionOverlay === 'function'
      ? system.drawInteractionOverlay
      : null,
    getProjectObjects: typeof system.getProjectObjects === 'function' ? system.getProjectObjects : null,
    getDrawingObjects: typeof system.getDrawingObjects === 'function' ? system.getDrawingObjects : null,
    getProjectBounds: typeof system.getProjectBounds === 'function' ? system.getProjectBounds : null,
    // Projektweiter Wireframe-/STEP-Export (view3d_controller.js), siehe
    // system_hooks.js's collectWireframeExportItems()/collectStepExportItems()/
    // ensureAllDxfShapesReady()/ensureAllStepExportReady() -- gleiches Opt-in-Muster wie getProjectObjects().
    getWireframeExportItems: typeof system.getWireframeExportItems === 'function' ? system.getWireframeExportItems : null,
    getStepExportItems: typeof system.getStepExportItems === 'function' ? system.getStepExportItems : null,
    ensureDxfShapesReady: typeof system.ensureDxfShapesReady === 'function' ? system.ensureDxfShapesReady : null,
    ensureStepExportReady: typeof system.ensureStepExportReady === 'function' ? system.ensureStepExportReady : null,
    get3DSceneSignature: typeof system.get3DSceneSignature === 'function' ? system.get3DSceneSignature : null,
    get3DPerformanceStats: typeof system.get3DPerformanceStats === 'function' ? system.get3DPerformanceStats : null,
    getCoreGroups: typeof system.getCoreGroups === 'function' ? system.getCoreGroups : null,
    selectCoreGroup: typeof system.selectCoreGroup === 'function' ? system.selectCoreGroup : null,
    getCoreGroupSidebarActions: typeof system.getCoreGroupSidebarActions === 'function'
      ? system.getCoreGroupSidebarActions
      : null,
    executeCoreGroupSidebarAction: typeof system.executeCoreGroupSidebarAction === 'function'
      ? system.executeCoreGroupSidebarAction
      : null,
    snapCoreGroupRotation: typeof system.snapCoreGroupRotation === 'function'
      ? system.snapCoreGroupRotation
      : null,
    finalizeCoreGroupRotation: typeof system.finalizeCoreGroupRotation === 'function'
      ? system.finalizeCoreGroupRotation
      : null,
    getSidebarRows: typeof system.getSidebarRows === 'function' ? system.getSidebarRows : null,
    selectSidebarRow: typeof system.selectSidebarRow === 'function' ? system.selectSidebarRow : null,
    setSidebarRowVisibility: typeof system.setSidebarRowVisibility === 'function' ? system.setSidebarRowVisibility : null,
    getCoreObjects: typeof system.getCoreObjects === 'function' ? system.getCoreObjects : null,
    selectCoreObject: typeof system.selectCoreObject === 'function' ? system.selectCoreObject : null,
    setCoreObjectVisibility: typeof system.setCoreObjectVisibility === 'function'
      ? system.setCoreObjectVisibility
      : null,
    getObjectLibraryItems: typeof system.getObjectLibraryItems === 'function'
      ? system.getObjectLibraryItems
      : null,
    getSnapPoints: typeof system.getSnapPoints === 'function' ? system.getSnapPoints : null,
    collectBoxSelection: typeof system.collectBoxSelection === 'function' ? system.collectBoxSelection : null,
    selectBoxSelection: typeof system.selectBoxSelection === 'function' ? system.selectBoxSelection : null,
    validatePlacement: typeof system.validatePlacement === 'function' ? system.validatePlacement : null,
    getBomSortRank: typeof system.getBomSortRank === 'function' ? system.getBomSortRank : null,
    getCanvasCommandStatus: typeof system.getCanvasCommandStatus === 'function'
      ? system.getCanvasCommandStatus
      : null,
    syncActiveToolFromLegacy: typeof system.syncActiveToolFromLegacy === 'function'
      ? system.syncActiveToolFromLegacy
      : null,
    applyActivationDefaults: typeof system.applyActivationDefaults === 'function'
      ? system.applyActivationDefaults
      : null,
    handleToolAction: toolActionHandler,
    handleToolbarAction: toolActionHandler,
    isToolbarActionActive: typeof system.isToolbarActionActive === 'function'
      ? system.isToolbarActionActive
      : null,
    isToolbarActionSelected: typeof system.isToolbarActionSelected === 'function'
      ? system.isToolbarActionSelected
      : null,
    getBomItems: bomFactory,
    getBomScopes: bomScopeFactory,
    createBomRows: bomFactory,
    statisticsAdapter: system.statisticsAdapter && typeof system.statisticsAdapter === 'object'
      ? system.statisticsAdapter
      : null,
    multiSelectionFields: boundaryClaims.multiSelectionFields,
    boxSelectionPassthroughFields: boundaryClaims.boxSelectionPassthroughFields,
    resolveItemToMultiSelection: typeof system.resolveItemToMultiSelection === 'function' ? system.resolveItemToMultiSelection : null,
    resolveHitToMultiSelection: typeof system.resolveHitToMultiSelection === 'function' ? system.resolveHitToMultiSelection : null,
    resolveHitToItems: typeof system.resolveHitToItems === 'function' ? system.resolveHitToItems : null,
    mergeLegacySelection: typeof system.mergeLegacySelection === 'function' ? system.mergeLegacySelection : null,
    syncLegacySelectionFields: typeof system.syncLegacySelectionFields === 'function' ? system.syncLegacySelectionFields : null,
    deduplicateMultiSelectionField: typeof system.deduplicateMultiSelectionField === 'function' ? system.deduplicateMultiSelectionField : null,
    clipboardActivationPriority: typeof system.clipboardActivationPriority === 'number' ? system.clipboardActivationPriority : null,
    clipboardCommitMode: system.clipboardCommitMode === 'owner' ? 'owner' : null,
    selectionCommitMode: system.selectionCommitMode === 'owner' ? 'owner' : null,
    legacyClipboardKind: boundaryClaims.legacyClipboardKind,
    getLegacyClipboardEntry: typeof system.getLegacyClipboardEntry === 'function' ? system.getLegacyClipboardEntry : null,
    pasteLegacyPayload: typeof system.pasteLegacyPayload === 'function' ? system.pasteLegacyPayload : null
  });
  systemRegistryRevision = nextRevision(systemRegistryRevision);
  return true;
}

export function registerLazySystem(system, loader, options = {}) {
  if (!system?.id || typeof loader !== 'function') return false;
  if (systems.has(system.id) || lazySystems.has(system.id)) return false;
  const projectContentPaths = Array.isArray(options.projectContentPaths)
    ? options.projectContentPaths.map(path => Array.isArray(path) ? [...path] : []).filter(path => path.length)
    : [];
  lazySystems.set(system.id, {
    id: system.id,
    loader,
    projectContentPaths,
    promise: null,
    loaded: false
  });
  try {
    const registered = registerSystem({
      ...system,
      meta: {
        ...(system.meta && typeof system.meta === 'object' ? system.meta : {}),
        lazyProxy: true
      }
    });
    if (registered === true) return true;
    lazySystems.delete(system.id);
    return false;
  } catch (error) {
    lazySystems.delete(system.id);
    throw error;
  }
}

export function isSystemLoaded(systemId) {
  const entry = lazySystems.get(systemId);
  return !entry || entry.loaded === true;
}

export function ensureSystemLoaded(systemId) {
  const entry = lazySystems.get(systemId);
  if (!entry) return Promise.resolve(getSystem(systemId));
  if (entry.loaded) return Promise.resolve(getSystem(systemId));
  if (!entry.promise) {
    entry.promise = Promise.resolve()
      .then(() => entry.loader())
      .then(() => {
        const system = getSystem(systemId);
        if (!system || system.meta?.lazyProxy === true) {
          throw new Error(`Systemimplementierung wurde nicht registriert: ${systemId}`);
        }
        entry.loaded = true;
        // The loader's own registerSystem() call (above) just replaced this
        // system's registry entry wholesale, discarding any label/icon/active
        // overlay applyHeaderConfig had applied to its placeholder. Restore
        // it here — the single choke point every caller of
        // ensureSystemLoaded goes through (navigation AND boot-time
        // ensureSystemsForProject alike) — rather than requiring each caller
        // to remember to do it.
        reapplyLastHeaderConfig();
        return system;
      })
      .catch(error => {
        entry.promise = null;
        throw error;
      });
  }
  return entry.promise;
}

function projectCollectionHasContent(project, path) {
  let value = project;
  for (const segment of path) {
    if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
    value = value[segment];
  }
  return Array.isArray(value) && value.length > 0;
}

export function systemIdsForProject(project, options = {}) {
  const ids = new Set();
  const activeSystemId = String(options.activeSystemId || '').trim();
  if (activeSystemId && lazySystems.has(activeSystemId)) ids.add(activeSystemId);
  if (!project || typeof project !== 'object' || Array.isArray(project)) return [...ids];
  for (const entry of lazySystems.values()) {
    if (entry.projectContentPaths.some(path => projectCollectionHasContent(project, path))) ids.add(entry.id);
  }
  return [...ids];
}

export async function ensureSystemsForProject(project, options = {}) {
  const systemIds = systemIdsForProject(project, options);
  await Promise.all(systemIds.map(systemId => ensureSystemLoaded(systemId)));
  return systemIds.map(systemId => getSystem(systemId)).filter(Boolean);
}

/**
 * Liefert die aktuell aktive Firma aus der Registry.
 *
 * @returns {Object|null} Aktive Firma oder `null`, falls noch keine Firma registriert ist.
 */
export function activeCompany() {
  return companies.get(state.activeCompanyId) || null;
}

/**
 * Beschreibt active Category im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function activeCategory() {
  return categories.get(state.activeCategoryId) || null;
}

/**
 * Beschreibt active System im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function activeSystem() {
  return systems.get(state.activeSystemId) || null;
}

/**
 * Sammelt get System im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} systemId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function getSystem(systemId) {
  return systems.get(systemId) || null;
}

function normalizeSystemAliases(aliases = []) {
  if (!Array.isArray(aliases)) return [];
  return [...new Set(
    aliases
      .map(alias => String(alias || '').trim())
      .filter(Boolean)
  )];
}

export function normalizeSystemId(value) {
  const raw = String(value || '').trim();
  if (!raw) return '';
  if (systems.has(raw)) return raw;
  const lower = raw.toLowerCase();
  for (const system of systems.values()) {
    if (String(system.id || '').toLowerCase() === lower) return system.id;
    if ((system.aliases || []).some(alias => String(alias || '').toLowerCase() === lower)) return system.id;
  }
  return lower;
}

/**
 * Liefert eine registrierte Firma nach ID.
 *
 * @param {string} companyId - ID der Firma.
 * @returns {Object|null} Registrierte Firma oder `null`.
 */
export function getCompany(companyId) {
  return companies.get(companyId) || null;
}

/**
 * Liefert eine registrierte Hauptkategorie nach ID.
 *
 * @param {string} categoryId - ID der Kategorie.
 * @returns {Object|null} Registrierte Kategorie oder `null`.
 */
export function getCategory(categoryId) {
  return categories.get(categoryId) || null;
}

/**
 * Liefert alle registrierten Firmen fuer die erste Header-Zeile.
 *
 * @returns {Object[]} Firmen in Registrierungsreihenfolge.
 */
export function allCompanies() {
  return Array.from(companies.values()).filter(company => company.active !== false);
}

/**
 * Sammelt all Systems im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function allSystems() {
  return Array.from(systems.values());
}

export function getSystemRegistryRevision() {
  return systemRegistryRevision;
}

/**
 * Ueberlagert die hart codierte Registry mit Anzeige-Metadaten (Label, Bild,
 * aktiv/inaktiv, Zuordnung) aus der Datenbank (`/api/header-config`).
 *
 * Zweck:
 * - Company/Category/System bleiben Code (Verhalten, Rendering, Werkzeuge);
 *   nur Anzeige und Sichtbarkeit werden datengetrieben.
 * - Ein `code`, der zu keiner hart codierten Registrierung passt, erzeugt
 *   einen reinen Platzhaltereintrag ohne Verhalten (kein `render2D`/`render3D`/
 *   `getPropertiesPanel` etc. - jeder Aufrufer dieser Felder nutzt bereits
 *   optionales Chaining, ein Platzhalter ist daher gefahrlos).
 * - Vor dem ersten erfolgreichen Aufruf bleibt das heutige, rein hart codierte
 *   Verhalten unveraendert (siehe `headerConfigLoaded`-Weiche in
 *   `categoriesForCompany`/`systemsForCategory`).
 *
 * @param {Object} config - Antwort von `/api/header-config`.
 */
// Cached so a lazy system's real module — which fully replaces its registry
// entry via a plain registerSystem() call the moment it finishes loading
// (see registerLazySystem/ensureSystemLoaded below), discarding whatever
// label/icon/active overlay applyHeaderConfig had already patched onto its
// placeholder — can have that overlay re-applied afterward. See
// reapplyLastHeaderConfig, called from
// system_navigation.js#loadSystemAfterNavigation.
let lastAppliedHeaderConfig = null;

export function applyHeaderConfig(config = {}) {
  lastAppliedHeaderConfig = config;
  const companiesData = Array.isArray(config.companies) ? config.companies : [];
  const categoriesData = Array.isArray(config.categories) ? config.categories : [];
  const systemsData = Array.isArray(config.systems) ? config.systems : [];
  const companyCategoryData = Array.isArray(config.companyCategories) ? config.companyCategories : [];
  const categorySystemData = Array.isArray(config.categorySystems) ? config.categorySystems : [];

  for (const entry of companiesData) {
    const code = String(entry?.code || '').trim();
    if (!code) continue;
    const existing = companies.get(code);
    companies.set(code, {
      id: code,
      label: entry.label || existing?.label || code,
      logoSrc: entry.image_path || existing?.logoSrc || null,
      logoAlt: entry.label || existing?.logoAlt || code,
      active: entry.active_flag !== false
    });
  }

  for (const entry of categoriesData) {
    const code = String(entry?.code || '').trim();
    if (!code) continue;
    const existing = categories.get(code);
    categories.set(code, {
      id: code,
      companyId: existing?.companyId || null,
      label: entry.label || existing?.label || code,
      active: entry.active_flag !== false
    });
  }

  for (const entry of systemsData) {
    const code = String(entry?.code || '').trim();
    if (!code) continue;
    const existing = systems.get(code);
    if (existing) {
      existing.label = entry.label || existing.label;
      existing.name = existing.label;
      existing.icon = entry.image_path || existing.icon;
      existing.active = entry.active_flag !== false;
    } else {
      systems.set(code, {
        id: code,
        categoryId: null,
        companyId: null,
        name: entry.label || code,
        label: entry.label || code,
        icon: entry.image_path || null,
        active: entry.active_flag !== false,
        aliases: [],
        meta: { placeholder: true },
        hiddenFromNavigation: false,
        toolbarButtons: [],
        selectionPriority: 1000
      });
    }
  }

  companyCategoryLinks.clear();
  for (const link of companyCategoryData) {
    const companyId = String(link?.companyCode || '').trim();
    const categoryId = String(link?.categoryCode || '').trim();
    if (!companyId || !categoryId) continue;
    if (!companyCategoryLinks.has(companyId)) companyCategoryLinks.set(companyId, new Set());
    companyCategoryLinks.get(companyId).add(categoryId);
  }

  categorySystemLinks.clear();
  for (const link of categorySystemData) {
    const categoryId = String(link?.categoryCode || '').trim();
    const systemId = String(link?.systemCode || '').trim();
    if (!categoryId || !systemId) continue;
    if (!categorySystemLinks.has(categoryId)) categorySystemLinks.set(categoryId, new Set());
    categorySystemLinks.get(categoryId).add(systemId);
  }

  headerConfigLoaded = true;
  systemRegistryRevision = nextRevision(systemRegistryRevision);
}

/**
 * Wendet die zuletzt geladene Header-Konfiguration erneut an.
 *
 * Zweck:
 * - ein lazy geladenes System ersetzt seinen Registry-Eintrag beim Laden
 *   vollstaendig (siehe registerLazySystem/ensureSystemLoaded) und wirft damit
 *   jede zuvor von applyHeaderConfig gesetzte Label-/Icon-/Active-Ueberlagerung
 *   weg; dieser Aufruf stellt sie wieder her.
 *
 * @returns {void}
 */
export function reapplyLastHeaderConfig() {
  if (lastAppliedHeaderConfig) applyHeaderConfig(lastAppliedHeaderConfig);
}

/**
 * Filtert Hauptkategorien nach Firma.
 *
 * Zweck:
 * - verhindert die Vermischung von Kategorien unterschiedlicher Firmen
 * - liefert die Datenbasis fuer die zweite Header-Zeile
 *
 * @param {string} companyId - Aktive Firma.
 * @returns {Object[]} Kategorien der Firma.
 */
export function categoriesForCompany(companyId) {
  const source = headerConfigLoaded
    ? Array.from(companyCategoryLinks.get(companyId) || []).map(id => categories.get(id)).filter(Boolean)
    : Array.from(categories.values()).filter(category => category.companyId === companyId);
  return source.filter(category => category.active !== false);
}

/**
 * Beschreibt systems For Category im Bereich Kernanwendung.
 *
 * Zweck:
 * - Unterstützt Systemregistrierung, Toolbar und Integration.
 * - Kapselt fachliche Hilfslogik im jeweiligen Modul, damit Aufrufer nicht die Moduldetails kennen müssen.
 * @param {*} categoryId - Eindeutige ID zum Auffinden des betroffenen Objekts.
 * @returns {*} Berechnetes Ergebnis, gefundenes Objekt oder Statuswert; `null`/`false`, wenn keine gültige fachliche Lösung möglich ist.
 */
export function systemsForCategory(categoryId) {
  const source = headerConfigLoaded
    ? Array.from(categorySystemLinks.get(categoryId) || []).map(id => systems.get(id)).filter(Boolean)
    : Array.from(systems.values()).filter(system => (
      system.categoryId === categoryId
      && system.companyId === state.activeCompanyId
    ));
  return source.filter(system => system.hiddenFromNavigation !== true && system.active !== false);
}

/**
 * Korrigiert aktive IDs, wenn Firma, Kategorie oder Produkt nicht zusammenpassen.
 *
 * Zweck:
 * - schuetzt die Header-Ebenen vor Vermischung
 * - waehlt beim Firmenwechsel automatisch die erste passende Kategorie und das erste passende Produkt
 *
 * @sideEffects Aktualisiert `state.activeCompanyId`, `state.activeCategoryId` und `state.activeSystemId`.
 */
export function normalizeNavigationState() {
  if (!companies.has(state.activeCompanyId)) {
    state.activeCompanyId = allCompanies()[0]?.id || null;
  }
  const category = categories.get(state.activeCategoryId);
  if (!category || category.companyId !== state.activeCompanyId) {
    const firstCategory = categoriesForCompany(state.activeCompanyId)[0] || null;
    state.activeCategoryId = firstCategory?.id || null;
  }
  const system = systems.get(state.activeSystemId);
  if (!system || system.companyId !== state.activeCompanyId || system.categoryId !== state.activeCategoryId) {
    const firstSystem = systemsForCategory(state.activeCategoryId)[0] || null;
    state.activeSystemId = firstSystem?.id || null;
  }
}
